import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  Refresh
} from '@mui/icons-material';
import { LayoutDefinition, PreviewPanelProps, Widget } from '../types';
import { absoluteOrigin, widgetsOf } from '../normalize';
import { valuePercent } from '../lvglRender';
import { useI18n } from '../../i18n';
import { LvglCanvasEditor } from './LvglCanvasEditor';

interface WidgetInteractionProps {
  widget: Widget;
  path: number[];
  selectedPathKey?: string;
  zoom: number;
  bounds: { width: number; height: number };
  layout: LayoutDefinition;
  onWidgetClick: (widget: Widget, path: number[]) => void;
  onWidgetDrag: (updatedWidget: Widget, path: number[], deltaX: number, deltaY: number) => void;
  onWidgetDrop?: (path: number[], absX: number, absY: number) => void;
}

function ink(name?: string, fallback = '#111'): string {
  const map: Record<string, string> = {
    white: '#fff',
    black: '#111',
    dark: '#212121',
    grey: '#e0e0e0',
    red: '#f44336',
    blue: '#2196f3',
    cyan: '#00bcd4',
    green: '#4caf50',
    orange: '#ff9800',
    purple: '#9c27b0',
  };
  if (name && map[name]) return map[name];
  return fallback;
}

function widgetCaption(widget: Widget, fallback: string): string {
  const text = String(widget.text || '').trim();
  if (text) {
    return text;
  }
  const id = String(widget.id || '').trim();
  if (id) {
    return id;
  }
  return fallback;
}

function WidgetFace({ widget, fallback }: { widget: Widget; fallback: string }) {
  const caption = widgetCaption(widget, fallback);
  const type = widget.type;

  if (type === 'icon') {
    const size = Math.max(14, Math.floor(Math.min(widget.width || 48, widget.height || 48) * 0.55));
    return (
      <span style={{ fontSize: size, lineHeight: 1, display: 'block', textAlign: 'center', width: '100%' }}>
        {caption || '◆'}
      </span>
    );
  }

  if (type === 'progress') {
    const pct = valuePercent(widget.text);
    return (
      <div style={{ width: '100%', height: '100%', boxSizing: 'border-box', background: ink(widget.background, '#fff'), border: '1px solid #888' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: ink(widget.color, '#111') }} />
      </div>
    );
  }

  if (type === 'switch') {
    const on = valuePercent(widget.text) > 0
      || /^(on|true|1)$/i.test(String(widget.text || '').trim());
    const track = on ? ink(widget.color, '#2196f3') : ink(widget.background, '#e0e0e0');
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 999,
          background: track,
          display: 'flex',
          alignItems: 'center',
          justifyContent: on ? 'flex-end' : 'flex-start',
          padding: 3,
          boxSizing: 'border-box',
          transition: 'background-color 0.25s ease',
        }}
      >
        <div
          style={{
            width: '42%',
            height: '100%',
            maxWidth: 28,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
            transition: 'transform 0.25s ease',
          }}
        />
      </div>
    );
  }

  if (type === 'gauge') {
    const pct = valuePercent(widget.text);
    const bar = ink(widget.color, '#2196f3');
    const angle = -135 + (pct / 100) * 270;
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: `conic-gradient(from 225deg, #4caf50 0 25%, #ff9800 25% 55%, #f44336 55% 75%, #e0e0e0 75% 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          boxShadow: 'inset 0 0 0 3px #bdbdbd',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: 2,
            height: '38%',
            background: bar,
            borderRadius: 1,
            transformOrigin: 'bottom center',
            transform: `rotate(${angle}deg)`,
            bottom: '50%',
            left: 'calc(50% - 1px)',
            transition: 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)',
            zIndex: 1,
          }}
        />
        <div
          style={{
            width: '62%',
            height: '62%',
            borderRadius: '50%',
            background: ink(widget.background, '#fff'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 600,
            color: bar,
            boxShadow: '0 0 0 2px #eee',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {caption || `${pct}`}
        </div>
      </div>
    );
  }

  return <>{caption}</>;
}

const InteractiveWidget: React.FC<WidgetInteractionProps> = ({
  widget,
  path,
  selectedPathKey,
  zoom,
  bounds,
  layout,
  onWidgetClick,
  onWidgetDrag,
  onWidgetDrop,
}) => {
  const { t } = useI18n();
  const isSelected = selectedPathKey === path.join('.');
  const draggingRef = useRef(false);
  const nested = Boolean(widget.children && widget.children.length);
  const isContainer = widget.type === 'container';
  const isChrome = widget.type === 'button' || widget.type === 'progress' || widget.type === 'gauge' || widget.type === 'switch';

  const startDrag = useCallback((clientX: number, clientY: number) => {
    const startX = widget.x;
    const startY = widget.y;
    const ww = widget.width || 100;
    const wh = widget.height || 20;
    const parentAbs = path.length > 1
      ? absoluteOrigin(widgetsOf(layout), path.slice(0, -1))
      : { x: 0, y: 0 };
    let lastAbsX = parentAbs.x + startX;
    let lastAbsY = parentAbs.y + startY;
    draggingRef.current = false;

    const onMove = (moveEvent: MouseEvent) => {
      const deltaX = Math.round((moveEvent.clientX - clientX) / zoom);
      const deltaY = Math.round((moveEvent.clientY - clientY) / zoom);
      if (!draggingRef.current && Math.abs(deltaX) < 2 && Math.abs(deltaY) < 2) {
        return;
      }
      draggingRef.current = true;
      // Clamp in display space so widgets can leave a container and land on another
      const absX = Math.max(0, Math.min(layout.display.width - ww, parentAbs.x + startX + deltaX));
      const absY = Math.max(0, Math.min(layout.display.height - wh, parentAbs.y + startY + deltaY));
      lastAbsX = absX;
      lastAbsY = absY;
      onWidgetDrag({
        ...widget,
        x: absX - parentAbs.x,
        y: absY - parentAbs.y,
      }, path, deltaX, deltaY);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('mouseup', onUp);
      if (draggingRef.current) {
        onWidgetDrop?.(path, lastAbsX, lastAbsY);
      }
      draggingRef.current = false;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('mouseup', onUp);
  }, [widget, path, zoom, onWidgetDrag, onWidgetDrop, layout, bounds]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onWidgetClick(widget, path);
    startDrag(e.clientX, e.clientY);
  }, [widget, path, onWidgetClick, startDrag]);

  const fill = (widget.type === 'label' || widget.type === 'icon') && !widget.background
    ? 'transparent'
    : (widget.type === 'progress' || widget.type === 'gauge' || widget.type === 'switch'
      ? 'transparent'
      : ink(widget.background, '#fff'));
  const style: React.CSSProperties = {
    position: 'absolute',
    left: widget.x,
    top: widget.y,
    width: widget.width || 100,
    height: widget.height || 20,
    backgroundColor: fill,
    color: ink(widget.color, '#111'),
    border: widget.type === 'button'
      ? 'none'
      : (isContainer ? '1px solid #90caf9' : 'none'),
    borderRadius: widget.type === 'button' ? 12
      : widget.type === 'gauge' ? '50%'
        : widget.type === 'progress' || widget.type === 'switch' ? 999
          : 0,
    boxShadow: widget.type === 'button' ? '0 3px 8px rgba(33,150,243,0.35)' : undefined,
    fontSize: 13,
    fontFamily: 'Montserrat, "DejaVu Sans", Arial, sans-serif',
    fontWeight: widget.type === 'button' ? 600 : undefined,
    display: nested ? 'block' : 'flex',
    alignItems: nested ? undefined : 'center',
    justifyContent: widget.align === 'center' || isChrome || widget.type === 'icon'
      ? 'center'
      : widget.align === 'right' ? 'flex-end' : 'flex-start',
    padding: nested || widget.type === 'progress' || widget.type === 'gauge' || widget.type === 'switch' ? 0 : 4,
    boxSizing: 'border-box',
    userSelect: 'none',
    cursor: 'move',
    zIndex: 2,
    overflow: isContainer ? 'visible' : undefined,
    outline: isSelected ? '2px solid #2196f3' : undefined,
    outlineOffset: -2,
  };

  return (
    <div
      role="button"
      aria-label={widgetCaption(widget, t('layout.sampleText'))}
      style={style}
      onMouseDown={handlePointerDown}
      title={t('layout.moveWidget')}
    >
      {!nested && <WidgetFace widget={widget} fallback={t('layout.sampleText')} />}
      {widget.children?.map((child, index) => (
        <InteractiveWidget
          key={child.id || index}
          widget={child}
          path={[...path, index]}
          selectedPathKey={selectedPathKey}
          zoom={zoom}
          bounds={{ width: widget.width || 100, height: widget.height || 20 }}
          onWidgetClick={onWidgetClick}
          onWidgetDrag={onWidgetDrag}
          onWidgetDrop={onWidgetDrop}
          layout={layout}
        />
      ))}
    </div>
  );
};

export const PreviewPanel: React.FC<PreviewPanelProps & {
  selectedWidget?: Widget;
  selectedWidgetPath?: number[];
  onWidgetSelect?: (widget: Widget, path: number[]) => void;
  onWidgetChange?: (updatedWidget: Widget, path: number[], deltaX: number, deltaY: number) => void;
  onWidgetDrop?: (path: number[], absX: number, absY: number) => void;
  emptyHint?: React.ReactNode;
}> = ({
  layout,
  zoom = 1,
  selectedWidgetPath,
  onWidgetSelect,
  onWidgetChange,
  onWidgetDrop,
  emptyHint,
}) => {
  const { t } = useI18n();
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const isLvgl = layout.kind === 'lvgl';
  const widgets = widgetsOf(layout);

  const handleZoomIn = useCallback(() => {
    setCurrentZoom((prev) => Math.min(prev * 1.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setCurrentZoom((prev) => Math.max(prev / 1.2, 0.1));
  }, []);

  const handleReset = useCallback(() => {
    setCurrentZoom(1);
  }, []);

  if (isLvgl) {
    return (
      <LvglCanvasEditor
        layout={layout}
        selectedWidgetPath={selectedWidgetPath}
        onWidgetSelect={onWidgetSelect}
        onWidgetChange={onWidgetChange}
        onWidgetDrop={onWidgetDrop}
        emptyHint={emptyHint}
      />
    );
  }

  const displayStyle: React.CSSProperties = {
    width: layout.display.width,
    height: layout.display.height,
    backgroundColor: '#f7f4ea',
    border: '2px solid #111',
    position: 'relative',
    margin: '20px auto',
    transform: `scale(${currentZoom})`,
    transformOrigin: 'center center',
    overflow: 'hidden'
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('layout.preview', { width: layout.display.width, height: layout.display.height })}
        </Typography>
        <Box>
          <Tooltip title={t('layout.zoomIn')}>
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('layout.zoomOut')}>
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('layout.zoomReset')}>
            <IconButton onClick={handleReset} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: `${layout.display.height * 1.1}px`,
          position: 'relative',
        }}
      >
        <div style={displayStyle}>
          {widgets.map((widget, index) => (
            <InteractiveWidget
              key={widget.id || index}
              widget={widget}
              path={[index]}
              selectedPathKey={selectedWidgetPath?.join('.')}
              zoom={currentZoom}
              bounds={layout.display}
              onWidgetClick={(next, path) => onWidgetSelect?.(next, path)}
              onWidgetDrag={(next, path, dx, dy) => onWidgetChange?.(next, path, dx, dy)}
              onWidgetDrop={onWidgetDrop}
              layout={layout}
            />
          ))}
          {widgets.length === 0 && emptyHint}
        </div>
      </Box>

      <Typography variant="caption" color="textSecondary" display="block" mt={1}>
        {t('layout.previewHint', {
          zoom: Math.round(currentZoom * 100),
          type: t('layout.kindSimple'),
          colors: 'B/W/R',
        })}
      </Typography>
    </Paper>
  );
};
